import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, imageUrl } from "../lib/api";
import ImageUpload from "../components/ImageUpload";
import SortableItem from "../components/SortableItem";
import ConfirmDialog from "../components/ConfirmDialog";
import ExpiryDigestCard from "../components/ExpiryDigestCard";
import useToast from "../hooks/use-toast-admin";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { Plus, Pencil, Trash, X, Check, ShareNetwork, Copy } from "@phosphor-icons/react";

const emptyForm = {
  title: "",
  provider: "MTN",
  description: "",
  price: "",
  badge: "HOT DEAL",
  image_path: "",
  order: 0,
  active: true,
};

const PROVIDERS = ["MTN", "Vodacom", "Cell C", "Vox", "Telkom", "Other"];

export default function AdminOffersPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [err, setErr] = useState("");
  const [confirmTarget, setConfirmTarget] = useState(null); // { o, busy, err }
  const [filter, setFilter] = useState("all");
  const { showToast, ToastView } = useToast();

  // ---- filter logic ----
  const todayIso = new Date().toISOString().slice(0, 10);
  const sevenDaysIso = (() => {
    const d = new Date();
    d.setDate(d.getDate() + 7);
    return d.toISOString().slice(0, 10);
  })();

  const counts = useMemo(() => {
    const c = { all: items.length, active: 0, inactive: 0, soon: 0, expired: 0 };
    for (const o of items) {
      if (o.active) c.active += 1;
      else c.inactive += 1;
      if (o.expires_at) {
        if (o.expires_at < todayIso) c.expired += 1;
        else if (o.expires_at <= sevenDaysIso) c.soon += 1;
      }
    }
    return c;
  }, [items, todayIso, sevenDaysIso]);

  const visibleItems = useMemo(() => {
    switch (filter) {
      case "active":
        return items.filter((o) => o.active);
      case "inactive":
        return items.filter((o) => !o.active);
      case "soon":
        return items.filter(
          (o) => o.expires_at && o.expires_at >= todayIso && o.expires_at <= sevenDaysIso
        );
      case "expired":
        return items.filter((o) => o.expires_at && o.expires_at < todayIso);
      default:
        return items;
    }
  }, [items, filter, todayIso, sevenDaysIso]);
  const [saving, setSaving] = useState(false);

  const load = () =>
    api
      .get("/admin/offers")
      .then((r) => setItems(r.data || []))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing("new");
    setForm({ ...emptyForm, order: items.length });
    setErr("");
  };

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      title: o.title || "",
      provider: o.provider || "MTN",
      description: o.description || "",
      price: o.price || "",
      badge: o.badge || "",
      image_path: o.image_path || "",
      order: o.order || 0,
      active: !!o.active,
      expires_at: o.expires_at || "",
    });
    setErr("");
  };

  const close = () => {
    setEditing(null);
    setForm(emptyForm);
    setErr("");
  };

  const save = async () => {
    if (!form.title || !form.description || !form.image_path) {
      setErr("Title, description, and image are required.");
      return;
    }
    setSaving(true);
    setErr("");
    try {
      if (editing === "new") {
        await api.post("/offers", form);
      } else {
        await api.put(`/offers/${editing.id}`, form);
      }
      await load();
      close();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = (o) => setConfirmTarget({ o, busy: false, err: "" });

  const performDelete = async () => {
    if (!confirmTarget?.o) return;
    setConfirmTarget((c) => ({ ...c, busy: true, err: "" }));
    try {
      await api.delete(`/offers/${confirmTarget.o.id}`);
      const removed = confirmTarget.o;
      setConfirmTarget(null);
      setItems((prev) => prev.filter((x) => x.id !== removed.id));
      showToast("ok", `Deleted offer "${removed.title}".`);
      await load();
    } catch (e) {
      setConfirmTarget((c) => ({ ...c, busy: false, err: formatApiError(e) }));
    }
  };

  const toggle = async (o) => {
    await api.put(`/offers/${o.id}`, { active: !o.active });
    await load();
  };

  const duplicate = async (o) => {
    // Copy the offer as a DRAFT (not live) with a "(copy)" suffix, placed at
    // the end of the list. Admin can then tweak price/title and publish.
    const payload = {
      title: `${o.title} (copy)`,
      provider: o.provider,
      description: o.description,
      price: o.price,
      badge: o.badge,
      image_path: o.image_path,
      order: items.length,
      active: false,
      expires_at: o.expires_at || null,
    };
    try {
      const { data: created } = await api.post("/offers", payload);
      await load();
      showToast("ok", `Duplicated "${o.title}" — opening editor…`);
      // Immediately open the copy in the editor for quick tweaks
      openEdit(created);
    } catch (e) {
      showToast("err", formatApiError(e));
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    const next = arrayMove(items, oldIndex, newIndex).map((it, idx) => ({ ...it, order: idx }));
    setItems(next);
    try {
      await api.post("/offers/reorder", { ids: next.map((n) => n.id) });
    } catch {
      load();
    }
  };

  return (
    <div className="p-10" data-testid="admin-offers-page">
      <div className="flex items-end justify-between mb-10">
        <div>
          <div className="overline text-[#a8b2cc] mb-3">Special Offers</div>
          <h1 className="font-display text-5xl tracking-tighter">Offers</h1>
          <p className="mt-3 text-[#a8b2cc] max-w-xl">
            Manage promotional deals shown on the public site. Inactive offers stay
            saved but hidden from customers.
          </p>
        </div>
        <button onClick={openNew} className="btn-brutalist" data-testid="add-offer-button">
          <Plus size={16} weight="bold" /> New Offer
        </button>
      </div>

      {loading ? (
        <div className="text-[#a8b2cc] font-mono-accent">Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-[#2c3854] bg-[#141e32] p-16 text-center">
          <div className="font-display text-3xl mb-3">No offers yet</div>
          <p className="text-[#a8b2cc] mb-6">Publish your first special offer.</p>
          <button onClick={openNew} className="btn-brutalist" data-testid="empty-add-offer">
            <Plus size={16} weight="bold" /> New Offer
          </button>
        </div>
      ) : (
        <>
          <ExpiryDigestCard />
          <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="offer-filter-pills">
            {[
              { key: "all", label: "All" },
              { key: "active", label: "Active" },
              { key: "inactive", label: "Hidden" },
              { key: "soon", label: "Expiring soon", accent: "#dda632" },
              { key: "expired", label: "Expired", accent: "#af3fa6" },
            ].map((f) => {
              const n = counts[f.key];
              const isActive = filter === f.key;
              const accent = f.accent || "#f5ecd9";
              return (
                <button
                  key={f.key}
                  onClick={() => setFilter(f.key)}
                  className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-[0.16em] font-nav font-semibold rounded-full border transition-colors ${
                    isActive
                      ? "text-[#070b14]"
                      : "border-[#2c3854] text-[#a8b2cc] hover:border-[#f5ecd9] hover:text-[#f5ecd9]"
                  }`}
                  style={
                    isActive
                      ? { background: accent, borderColor: accent }
                      : f.accent && n > 0
                      ? { borderColor: f.accent + "55", color: f.accent }
                      : {}
                  }
                  data-testid={`offer-filter-${f.key}`}
                >
                  {f.label}
                  <span
                    className={`text-[0.62rem] px-1.5 py-0.5 rounded-full ${
                      isActive ? "bg-[#070b14]/20" : "bg-[#0e1626]"
                    }`}
                  >
                    {n}
                  </span>
                </button>
              );
            })}
          </div>

          {visibleItems.length === 0 ? (
            <div className="border border-dashed border-[#2c3854] bg-[#141e32] p-12 text-center" data-testid="offer-filter-empty">
              <div className="font-display text-2xl mb-2">Nothing here</div>
              <p className="text-[#a8b2cc] text-sm">No offers match the "{filter}" filter.</p>
            </div>
          ) : (
            <>
              {filter === "all" && (
                <div className="mb-4 font-mono-accent text-[0.72rem] uppercase tracking-[0.22em] text-[#a8b2cc]">
                  Drag the handle on each card to reorder
                </div>
              )}
              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={visibleItems.map((o) => o.id)} strategy={rectSortingStrategy}>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
                    {visibleItems.map((o) => (
                  <SortableItem key={o.id} id={o.id} testIdPrefix="offer">
                    <article className="sharp-card flex flex-col" data-testid={`offer-card-${o.id}`}>
                      {o.image_path && (
                        <div className="relative">
                          <img src={imageUrl(o.image_path)} alt={o.title} className="w-full aspect-[16/10] object-cover border-b border-[#2c3854]" />
                          {o.badge && (
                            <span className="absolute top-3 left-3 bg-[#af3fa6] text-white overline px-3 py-1.5">
                              {o.badge}
                            </span>
                          )}
                        </div>
                      )}
                      <div className="p-6 flex-1 flex flex-col">
                        <div className="flex items-center justify-between mb-3">
                          <span className="overline text-[#a8b2cc]">{o.provider}</span>
                          <button
                            onClick={() => toggle(o)}
                            className={`overline px-3 py-1 border ${
                              o.active
                                ? "bg-[#141e32] text-white border-[#f5ecd9]"
                                : "bg-[#141e32] text-[#a8b2cc] border-[#2c3854]"
                            }`}
                            data-testid={`offer-toggle-${o.id}`}
                          >
                            {o.active ? "LIVE" : "DRAFT"}
                          </button>
                        </div>
                        <h3 className="font-display text-xl tracking-tight">{o.title}</h3>
                        <p className="text-sm text-[#a8b2cc] mt-2 line-clamp-3">{o.description}</p>
                        {o.price && (
                          <div className="mt-3 font-mono-accent text-[0.9rem]">{o.price}</div>
                        )}
                        {o.expires_at && (() => {
                          const today = new Date().toISOString().slice(0, 10);
                          const expired = o.expires_at < today;
                          const display = new Date(o.expires_at + "T00:00:00").toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" });
                          return (
                            <div
                              className={`mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[0.65rem] uppercase tracking-[0.16em] font-nav font-semibold border self-start ${
                                expired
                                  ? "bg-[#1c2942] text-[#af3fa6] border-[#af3fa6]"
                                  : "bg-[#1c2942] text-[#dda632] border-[#dda632]/40"
                              }`}
                              data-testid={`offer-expiry-${o.id}`}
                            >
                              {expired ? "Expired" : "Expires"} {display}
                            </div>
                          );
                        })()}
                        <div className="flex gap-2 mt-auto pt-4">
                          <button
                            onClick={() => openEdit(o)}
                            className="btn-ghost flex-1 justify-center"
                            data-testid={`offer-edit-${o.id}`}
                          >
                            <Pencil size={14} weight="bold" /> Edit
                          </button>
                          <button
                            onClick={() => duplicate(o)}
                            className="border border-[#dda632] text-[#dda632] hover:bg-[#dda632] hover:text-[#070b14] p-3 transition-colors"
                            data-testid={`offer-duplicate-${o.id}`}
                            aria-label="Duplicate offer"
                            title="Duplicate offer"
                          >
                            <Copy size={16} weight="bold" />
                          </button>
                          <button
                            onClick={() => {
                              const url = `https://infinity-dms.co.za/api/social/offer/${o.id}`;
                              navigator.clipboard?.writeText(url);
                              alert(`Share URL copied:\n${url}\n\nPaste this on WhatsApp, Facebook, etc. — social cards will show the offer image and details.`);
                            }}
                            className="border border-[#55a2d2] text-[#55a2d2] hover:bg-[#55a2d2] hover:text-white p-3 transition-colors"
                            data-testid={`offer-share-${o.id}`}
                            aria-label="Copy social share URL"
                            title="Copy social share URL"
                          >
                            <ShareNetwork size={16} weight="bold" />
                          </button>
                          <button
                            onClick={() => remove(o)}
                            className="border border-[#af3fa6] text-[#af3fa6] hover:bg-[#af3fa6] hover:text-white p-3 transition-colors"
                            data-testid={`offer-delete-${o.id}`}
                            aria-label="Delete offer"
                          >
                            <Trash size={16} weight="bold" />
                          </button>
                        </div>
                      </div>
                    </article>
                  </SortableItem>
                ))}
                  </div>
                </SortableContext>
              </DndContext>
            </>
          )}
        </>
      )}

      {editing && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-6" onClick={close}>
          <div
            className="bg-[#141e32] border border-[#f5ecd9] w-full max-w-3xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="offer-editor-modal"
          >
            <div className="flex items-center justify-between p-6 border-b border-[#2c3854]">
              <div>
                <div className="overline text-[#a8b2cc] mb-1">{editing === "new" ? "New" : "Edit"} Offer</div>
                <h2 className="font-display text-3xl tracking-tighter">
                  {editing === "new" ? "Create offer" : form.title}
                </h2>
              </div>
              <button onClick={close} className="p-2 hover:bg-[#070b14]" data-testid="offer-editor-close">
                <X size={20} />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {err && (
                <div className="border border-[#af3fa6] text-[#af3fa6] p-3 text-sm" data-testid="offer-editor-error">
                  {err}
                </div>
              )}

              <FieldLabel label="Image">
                <ImageUpload
                  value={form.image_path}
                  onChange={(p) => setForm({ ...form, image_path: p })}
                  testid="offer-image-upload"
                />
              </FieldLabel>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <FieldLabel label="Title">
                  <input
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="admin-input"
                    data-testid="offer-input-title"
                  />
                </FieldLabel>

                <FieldLabel label="Provider">
                  <select
                    value={form.provider}
                    onChange={(e) => setForm({ ...form, provider: e.target.value })}
                    className="admin-input"
                    data-testid="offer-input-provider"
                  >
                    {PROVIDERS.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </FieldLabel>
              </div>

              <FieldLabel label="Description">
                <textarea
                  rows={4}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="admin-input resize-none"
                  data-testid="offer-input-description"
                />
              </FieldLabel>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <FieldLabel label="Price / Tariff">
                  <input
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    placeholder="R299 pm"
                    className="admin-input"
                    data-testid="offer-input-price"
                  />
                </FieldLabel>
                <FieldLabel label="Badge">
                  <input
                    value={form.badge}
                    onChange={(e) => setForm({ ...form, badge: e.target.value })}
                    placeholder="HOT DEAL"
                    className="admin-input"
                    data-testid="offer-input-badge"
                  />
                </FieldLabel>
                <FieldLabel label="Order">
                  <input
                    type="number"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || "0", 10) })}
                    className="admin-input"
                    data-testid="offer-input-order"
                  />
                </FieldLabel>
              </div>

              <FieldLabel label="Expires on (optional)">
                <div className="flex items-center gap-3">
                  <input
                    type="date"
                    value={form.expires_at || ""}
                    onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                    min={new Date().toISOString().slice(0, 10)}
                    className="admin-input"
                    style={{ width: "auto" }}
                    data-testid="offer-input-expires-at"
                  />
                  {form.expires_at && (
                    <button
                      type="button"
                      onClick={() => setForm({ ...form, expires_at: "" })}
                      className="text-[0.7rem] uppercase tracking-[0.18em] font-nav font-semibold text-[#a8b2cc] hover:text-[#af3fa6]"
                      data-testid="offer-input-expires-clear"
                    >
                      Clear
                    </button>
                  )}
                </div>
                <div className="text-[0.7rem] text-[#a8b2cc] mt-1.5">
                  Offer is hidden from the public site after this date. Leave empty for no expiry.
                </div>
              </FieldLabel>

              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  data-testid="offer-input-active"
                />
                <span className="font-mono-accent text-[0.8rem] uppercase tracking-[0.18em]">
                  {form.active ? "Publish live on site" : "Save as draft"}
                </span>
              </label>
            </div>

            <div className="p-6 border-t border-[#2c3854] flex gap-3 justify-end">
              <button onClick={close} className="btn-ghost" data-testid="offer-editor-cancel">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="btn-brutalist disabled:opacity-60"
                data-testid="offer-editor-save"
              >
                <Check size={16} weight="bold" /> {saving ? "Saving…" : "Save offer"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="Delete this offer?"
        description={
          confirmTarget?.o ? (
            <>
              Permanently removes the offer{" "}
              <span className="text-[#f5ecd9]">"{confirmTarget.o.title}"</span>
              {confirmTarget.o.provider ? ` (${confirmTarget.o.provider})` : ""} from the
              public listing. This cannot be undone.
            </>
          ) : null
        }
        confirmLabel="Yes, delete offer"
        onCancel={() => setConfirmTarget(null)}
        onConfirm={performDelete}
        busy={confirmTarget?.busy}
        error={confirmTarget?.err}
        testId="offer-confirm-delete"
      />
      <ToastView />
    </div>
  );
}

const FieldLabel = ({ label, children }) => (
  <label className="block">
    <span className="block font-mono-accent text-[0.7rem] uppercase tracking-[0.22em] text-[#a8b2cc] mb-2">
      {label}
    </span>
    {children}
  </label>
);
